import { jsx as _jsx } from "react/jsx-runtime";
import { forwardRef } from 'react';
import { Moon } from '@osd/icons/moon';
import { Sun } from '@osd/icons/sun';
/**
 * ThemeToggle — single icon-only button that flips between light and
 * dark colour palettes. Uses our own `Sun` + `Moon` icons from
 * `@osd/icons` (zero-runtime-dep SVG components).
 *
 * The toggle renders a ghost `Button` so it inherits the existing
 * focus ring, hover, disabled, and active treatments without
 * re-implementing them. Size matches the standard `.osd-button`
 * padding; override with `className` if you need a larger tap target
 * (the AppShell header doesn't).
 *
 * Pair with the `useTheme()` hook from `@osd/web` (or your own
 * equivalent — the contract is just `{ resolved, toggle }`):
 *
 * @example
 *   const { resolved, toggle } = useTheme();
 *   <ThemeToggle resolved={resolved} onClick={toggle} />
 */
export const ThemeToggle = forwardRef(function ThemeToggle({ resolved, ariaLabel, className, ...rest }, ref) {
    // Icon + label flip together: in light mode we show the moon
    // (because the action is "go dark"); in dark mode we show the sun.
    // This matches the convention used by GitHub, Linear, and Vercel —
    // the glyph signals the destination, not the current state.
    const isLight = resolved === 'light';
    const Glyph = isLight ? Moon : Sun;
    const defaultLabel = isLight ? 'Switch to dark mode' : 'Switch to light mode';
    const classes = ['osd-theme-toggle', className].filter(Boolean).join(' ');
    return (_jsx("button", { ref: ref, type: "button", "data-osd-theme-toggle": resolved, className: classes, "aria-label": ariaLabel ?? defaultLabel, title: ariaLabel ?? defaultLabel, ...rest, children: _jsx(Glyph, { "aria-hidden": "true", focusable: "false" }) }));
});
//# sourceMappingURL=ThemeToggle.js.map